import { useEffect, useState } from "react";
import DashboardContentLayout from "../../components/dashboard-content-layout";
import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { ControlledSelect, ControlledSelectOption, makeSxStyles } from "ui";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import {
  AdminDriverResViewDto,
  AdminPostRegionReqDto,
  AdminRegionLocationReqDto,
} from "service/api/estabrookSchemas";
import {
  useDriverControllerGetAll,
  useAdminRegionV2ControllerCreateRegion,
  useAdminRegionV2ControllerGetRegionById,
  useAdminRegionV2ControllerUpdateRegion,
  adminPutRegionReqDtoSchema,
  useAdminRegionV2ControllerDeleteRegionById,
} from "service";
import CustomColorSelector, {
  regColorOptions,
} from "../../../../components/color-selector/color-selector";
import { toast } from "react-toastify";
import { queryClient } from "../../../../query-client";
import {
  createRegionsInvalidationPredicate,
  createLocationsInvalidationPredicate,
} from "service/api/estabrookQueryKeys";
import { useNavigate, useParams } from "react-router-dom";
import { AdRoutes } from "../../../../constants/ad-routes";
import useRegionManagementStore from "../../../../state/region-management-store";
import { useDialogStore } from "../../../../state/dialog-store";
import Navbar from "../../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../../shared/components/header-items/HeaderItems";
import useUnsavedChangesBlocker from "../../../../shared/hooks/useUnsavedChangesBlocker";
import RegionWorkflowStepper from "../../../../components/region-workflow-stepper";

const CreateRegionPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();
  const [dailyDrivers, setDailyDrivers] = useState<ControlledSelectOption[]>(
    [],
  );
  const { sequencedAddresses, resetAllSelected, setPendingFocusRegionId } =
    useRegionManagementStore();
  // const [submitSuccess, setSubmitSuccess] = useState(false);
  const [locations, setLocations] = useState<AdminRegionLocationReqDto[]>([]);
  const { regionId } = useParams<{
    regionId: string;
  }>();

  const { isLoading, data: driverData } = useDriverControllerGetAll({
    queryParams: {
      limit: 50,
      isAvailable: true,
      hasVehicle: true,
    },
  });

  const { mutate: updateRegion } = useAdminRegionV2ControllerUpdateRegion({
    onSuccess: () => {
      toast.success("Region Updated Successfully");
      resetForm();
      queryClient.invalidateQueries(createRegionsInvalidationPredicate());
      setTimeout(() => {
        // Set the region ID that should be focused when the region management page loads
        if (regionId) {
          setPendingFocusRegionId(regionId);
        }
        navigate(AdRoutes.regionManagement);
        resetAllSelected();
        resetForm();
      }, 500);
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutate: createRegion } = useAdminRegionV2ControllerCreateRegion({
    onSuccess: async (response) => {
      toast.success("Region Created Successfully");
      // Invalidate both regions and locations queries to update the map and table
      await Promise.all([
        queryClient.invalidateQueries(createRegionsInvalidationPredicate()),
        queryClient.invalidateQueries(createLocationsInvalidationPredicate()),
      ]);

      const createdRegionId = response.data?.id;

      setTimeout(() => {
        // Set the region ID that should be focused when the region management page loads
        if (createdRegionId) {
          setPendingFocusRegionId(createdRegionId);
        }

        navigate(AdRoutes.regionManagement);
        resetAllSelected();
        resetForm();
      }, 500);
    },
  });

  const { mutate: deleteRegion } = useAdminRegionV2ControllerDeleteRegionById({
    onSuccess: () => {
      toast.success("Region Deleted Successfully");
      queryClient.invalidateQueries(createRegionsInvalidationPredicate());
      setIsDeleting(false);
      resetForm();
      resetAllSelected();
      navigate(AdRoutes.regionManagement);
    },
    onError: () => {
      setIsDeleting(false);
    },
  });

  const { isLoading: isLoadingRegion, data: regionData } =
    useAdminRegionV2ControllerGetRegionById(
      {
        pathParams: {
          id: regionId || "",
        },
      },
      {
        enabled: !!regionId && !isDeleting,
      },
    );

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    resetForm,
    setFieldTouched,
    setFieldValue,
  } = useFormik<AdminPostRegionReqDto>({
    initialValues: {
      name: "",
      color: "",
      shiftId: "",
      suggestedDrivers: {
        dailyShiftDriverId: "NO_DRIVER",
      },
      locations: [],
    },
    validationSchema: toFormikValidationSchema(adminPutRegionReqDtoSchema),
    onSubmit: (values) => {
      setIsSubmitting(true);
      const driverId = values.suggestedDrivers.dailyShiftDriverId;
      const commonBody = {
        shiftId: "",
        name: values.name,
        color: values.color, // Use the hex color code from the form values
        suggestedDrivers: {
          dailyShiftDriverId: driverId === "NO_DRIVER" ? "" : driverId,
        },
      };
      if (regionId) {
        return updateRegion({
          body: commonBody,
          pathParams: {
            id: regionId,
          },
        });
      }
      createRegion({
        body: { ...commonBody, locations },
      });
    },
    validateOnBlur: false,
  });

  useEffect(() => {
    const filteredLocations = sequencedAddresses.map((item) => ({
      id: item.id,
      seqNumber: item.locationSequence ?? 0,
    }));

    if (filteredLocations) {
      setLocations(filteredLocations);
    }

    if (driverData && driverData?.data) {
      const driverOptions = driverData?.data.reduce(
        (acc: ControlledSelectOption[], driverData: AdminDriverResViewDto) => {
          acc.push({
            label: driverData.name,
            value: driverData.id,
          });
          return acc;
        },
        [] as ControlledSelectOption[],
      );

      // Add "No Driver" option at the beginning
      setDailyDrivers([
        {
          label: "No Driver",
          value: "NO_DRIVER",
        },
        ...driverOptions,
      ]);
    }
  }, [driverData]);

  useEffect(() => {
    if (!isLoadingRegion && regionId && regionData?.data) {
      const formattedColor = regColorOptions.find(
        (option) => option.label === regionData.data?.color,
      );
      const existingDriverId = regionData.data.suggestedDrivers[0]?.driverId;
      const colorToUse = formattedColor?.value || regionData.data.color;

      resetForm({
        values: {
          name: regionData.data.name,
          color: colorToUse,
          suggestedDrivers: {
            dailyShiftDriverId: existingDriverId || "NO_DRIVER",
          },
          shiftId: "",
          locations: regionData.data.locations.map((location) => ({
            id: location.locationId,
            seqNumber: location.locationSequence,
          })),
        },
      });
    }
  }, [regionData, isLoadingRegion]);

  const onDeleteRegion = () => {
    setDialog({
      title: "Delete Region",
      description: `Are you sure you want to delete region ${values?.name}? This will unassign all linked addresses and cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      sx: {
        "& .MuiPaper-root": {
          maxWidth: "400px",
        },
      },
      onConfirm: async (onClose) => {
        setIsDeleting(true);
        deleteRegion({ pathParams: { id: regionId || "" } });
        onClose();
      },
    });
  };

  useUnsavedChangesBlocker(dirty && !isSubmitting && !isDeleting);

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Region Management",
                url: AdRoutes.regionManagement,
              },
              {
                label: regionId ? "Edit Region Details" : "Create Region",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant="contained"
              onClick={submitForm}
              data-testid={
                regionId ? "save-region-button" : "create-region-button"
              }
            >
              {regionId ? "Save Changes" : "Create Region"}
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>

      {!regionId && <RegionWorkflowStepper />}

      <Typography variant="h5" marginTop={"30px"}>
        Assigned Driver Details
      </Typography>
      <Box sx={styles.gridDetailContainer}>
        <Typography>Default Driver</Typography>
        <ControlledSelect
          id="dailyShiftDriverId"
          name="dailyShiftDriverId"
          placeholderSelectEnabled
          placeholder="Select Driver"
          options={dailyDrivers}
          value={values.suggestedDrivers.dailyShiftDriverId}
          sx={styles.dropdownStyle}
          onBlur={handleBlur}
          onChange={(value) => {
            setFieldValue("suggestedDrivers.dailyShiftDriverId", value);
            setFieldTouched("suggestedDrivers.dailyShiftDriverId", true);
          }}
        />
      </Box>
      <Divider sx={{ mt: "40px", mb: "40px" }} />
      <Box sx={styles.gridDetailContainer}>
        <Typography>Region Name</Typography>
        <TextField
          id="name"
          type="text"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.name && errors.name}
          error={touched.name && !!errors.name}
          margin="dense"
          variant="outlined"
          inputProps={{ maxLength: 30 }}
          fullWidth
        />
        <Typography>Route Color</Typography>
        <CustomColorSelector
          id="color"
          name="color"
          placeholder="Select Color:"
          value={values.color}
          onBlur={handleBlur}
          onChange={(value) => {
            setFieldValue("color", value);
            setFieldTouched("color", true);
          }}
        />
      </Box>
      {regionId && (
        <>
          <Divider sx={styles.divider} />
          <Box sx={styles.gridDetailContainer}>
            <Typography fontWeight={600} alignSelf="start">
              Delete Region
            </Typography>
            <Box display="grid" gap="20px" alignItems="flex-start">
              <Typography variant="caption">
                Before deleting this region, please note that doing so will
                result in addresses in this region becoming unassigned.
              </Typography>
              <Button
                color="error"
                variant="outlined"
                sx={styles.deleteButton}
                onClick={onDeleteRegion}
                startIcon={<Delete />}
              >
                Delete Region
              </Button>
            </Box>
          </Box>
        </>
      )}
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(({ palette }) => ({
  customActionsWrapper: {
    display: "flex",
    flexGrow: "1",
  },
  stepperContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginRight: "auto",
    marginLeft: "auto",
  },
  orderWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    "& span": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginRight: "10px",
      width: "24px",
      height: "24px",
      borderRadius: "100%",
      backgroundColor: palette.grey[500],
    },

    "& span, & p": {
      color: palette.grey[300],
    },

    "&.active * ": {
      color: "#FFF",
    },
  },
  stepperDash: {
    display: "flex",
    margin: "0 8px",
    width: "26.29px",
    height: "1px",
    backgroundColor: palette.grey[200],
  },

  dropdownStyle: {
    borderRadius: "4px",
    border: "1px solid #8B9198",
    height: "53px",

    "& > div.MuiSelect-select": {
      backgroundColor: "transparent !important",
    },
  },
  gridDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "center",
  },
  divider: {
    mt: "40px",
  },
}));

export default CreateRegionPage;
